import React, { useEffect, useRef, useState } from "react";
import { withSidebar } from "../hoc/withSidebar";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import ERPSBaseButton from "../custom/inputs/ERPSBaseButton";
import DialogHeader from "../custom/inputs/ERPSBaseModelDialog";
import ConfirmationMessage from "../custom/containers/ConfirmationMesssage";
import ErrorMessage from "../custom/containers/ErrorMessage";
import actions from "../../actions";
import { getFiShortName } from "./BcrPendingApprovalsConstant";
import { BCR_ACTION_CODE, FI_SHORT_NAME_TO_KEY } from "../../constants/bcrReview.constants";
import { Dialog, DialogActions, DialogContent, HStack, TextField, Typography, VStack } from "../primitives";
import BcrReviewMatchSection from "./BcrReviewMatchSection";
import BcrReviewMultipleMatchSection from "./BcrReviewMultipleMatchSection";
import BcrReviewNoMatchSection from "./BcrReviewNoMatchSection";

const BcrReviewDetail = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    location: { state },
    history,
  } = props;

  const pendingBcr = state?.pendingBcr;
  const userId = useSelector((reduxState) => reduxState.loginUserReducer?.user?.userId);
  const approveRejectBcrData = useSelector((reduxState) => reduxState.pendingBcrReducer?.approveRejectBcrData);

  const [reviewerNote, setReviewerNote] = useState(pendingBcr?.reviewerNote || "");
  const [selectedLinkId, setSelectedLinkId] = useState(null);
  const [selectionError, setSelectionError] = useState("");
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [confirmActionCode, setConfirmActionCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const errorRef = useRef();
  const selectionErrorRef = useRef();
  const successRef = useRef();

  const notAvailableLabel = t("erps.billermanagement.bcrreview.notavailable", "Not available");

  useEffect(() => {
    if (errorRef.current) errorRef.current.focus();
  }, [errorMessage]);

  useEffect(() => {
    if (selectionErrorRef.current) selectionErrorRef.current.focus();
  }, [selectionError]);

  useEffect(() => {
    if (showSuccessMessage) {
      setIsSubmitted(true);
      if (successRef.current) successRef.current.focus();
    }
  }, [showSuccessMessage]);

  const navigateBack = () => {
    history.push("/bcr-pending-approvals", { componentAccess: state?.componentAccess, fromDetailPage: true });
  };

  if (!pendingBcr) {
    return (
      <div role="main">
        <VStack ml={3} spacing={2}>
          <Breadcrumb {...props} componentAccess={state?.componentAccess} />
          <Typography variant="body1">
            {t(
              "erps.billermanagement.bcrreview.norecord",
              "No pending biller cross reference record was selected. Please return to the list."
            )}
          </Typography>
          <ERPSBaseButton
            variant="secondary"
            label={t("erps.newuser.button.back", "Back")}
            onClick={navigateBack}
          />
        </VStack>
      </div>
    );
  }

  const links = pendingBcr.links || [];
  const linkCount = links.length;
  const incomingFiLabel = getFiShortName(pendingBcr.incomingBillerFi);
  const incomingFiKey = FI_SHORT_NAME_TO_KEY[incomingFiLabel];

  const getSelectedLink = () => {
    if (linkCount === 1) return links[0];
    if (linkCount > 1) return links.find((link) => String(link.id) === String(selectedLinkId));
    return undefined;
  };

  const handleSelectLink = (e) => {
    setSelectedLinkId(e.target.value);
    setSelectionError("");
  };

  const handleApproveClick = () => {
    if (linkCount > 1 && !selectedLinkId) {
      setSelectionError(
        t("erps.billermanagement.bcrreview.multiple.selectionrequired", "Please select at least one option.")
      );
      return;
    }
    setSelectionError("");
    setConfirmActionCode(BCR_ACTION_CODE.APPROVE);
    setIsConfirmDialogOpen(true);
  };

  const handleRejectClick = () => {
    setSelectionError("");
    setConfirmActionCode(BCR_ACTION_CODE.REJECT);
    setIsConfirmDialogOpen(true);
  };

  const getApproveConfirmationMessage = () => {
    if (linkCount === 0) {
      return t(
        "erps.billermanagement.bcrreview.confirm.approve.newid.message",
        { fi: incomingFiLabel, billerId: pendingBcr.incomingBillerId }
      );
    }
    const selectedLink = getSelectedLink();
    return t(
      "erps.billermanagement.bcrreview.confirm.approve.message",
      {
        fi: incomingFiLabel,
        billerId: pendingBcr.incomingBillerId,
        centralizedBillerId: selectedLink?.targetCentralizedBillerId,
      }
    );
  };

  const getRejectConfirmationMessage = () =>
    t(
      "erps.billermanagement.bcrreview.confirm.reject.message",
      { fi: incomingFiLabel, billerId: pendingBcr.incomingBillerId }
    );

  const getConfirmationMessageText = () =>
    confirmActionCode === BCR_ACTION_CODE.APPROVE
      ? getApproveConfirmationMessage()
      : getRejectConfirmationMessage();

  const getDialogHeader = () =>
      confirmActionCode === BCR_ACTION_CODE.APPROVE
          ? t("erps.billermanagement.bcrreview.confirm.approve.header")
          : t("erps.billermanagement.bcrreview.confirm.reject.header");

  const getCentralizedBillerIdForSuccessMessage = () =>
    approveRejectBcrData?.centralizedBillerId
    || approveRejectBcrData?.CentralizedBillerId
    || getSelectedLink()?.targetCentralizedBillerId;

  const getSuccessMessageText = () => {
    const centralizedBillerId = getCentralizedBillerIdForSuccessMessage();
    if (confirmActionCode === BCR_ACTION_CODE.APPROVE) {
      return t(
        "erps.billermanagement.bcrreview.success.approved",
        {
          reviewId: pendingBcr.reviewId,
          billerId: pendingBcr.incomingBillerId,
          centralizedBillerId,
        }
      );
    }
    return t("erps.billermanagement.bcrreview.success.rejected", {
      centralizedBillerId,
    });
  };

  const handleConfirm = () => {
    setIsConfirmDialogOpen(false);
    setLoading(true);
    setShowSuccessMessage(false);

    const selectedLink = getSelectedLink();


    const pendingBcrRequest = {
      actionCode: confirmActionCode,
      reviewId: pendingBcr.reviewId,
      bcrId: selectedLink?.billerCrossReferenceId,
      userId,
      reviewerNote,
    };

    dispatch(
      actions.pendingBcrActions.approveOrRejectPendingBcr(pendingBcrRequest, {
        setLoading,
        setShowSuccessMessage,
        setErrorMessage,
        t,
      })
    );
  };

  return (
    <div role="main">
      <VStack ml={3} spacing={3}>

        <Breadcrumb {...props} componentAccess={state?.componentAccess} />

        {showSuccessMessage && (
            <ConfirmationMessage
                message={getSuccessMessageText()}
                setShowMessage={setShowSuccessMessage}
                inputRef={successRef}
            />
        )}

        <ErrorMessage error={errorMessage} errorRef={errorRef} mt={2} />

        <Typography variant="h1">
          {t(
            "erps.billermanagement.bcrreview.header",
            "Transaction ID: {{reviewId}}, Incoming Biller FI: {{incomingBillerFi}}, Incoming Biller ID: {{incomingBillerId}}",
            {
              reviewId: pendingBcr.reviewId,
              incomingBillerFi: incomingFiLabel,
              incomingBillerId: pendingBcr.incomingBillerId,
            }
          )}
        </Typography>

        {linkCount === 0 && (
          <BcrReviewNoMatchSection
            t={t}
            pendingBcr={pendingBcr}
            incomingFiLabel={incomingFiLabel}
            notAvailableLabel={notAvailableLabel}
          />
        )}

        {linkCount === 1 && (
          <BcrReviewMatchSection
            t={t}
            link={links[0]}
            bcrDto={links[0]?.billerCrossReferenceDto}
            pendingBcr={pendingBcr}
            incomingFiKey={incomingFiKey}
            incomingFiLabel={incomingFiLabel}
            notAvailableLabel={notAvailableLabel}
            hideDuplicateWarning={showSuccessMessage}
          />
        )}

        {linkCount > 1 && (
          <>
            <BcrReviewMultipleMatchSection
              t={t}
              links={links}
              incomingFiKey={incomingFiKey}
              incomingFiLabel={incomingFiLabel}
              pendingBcr={pendingBcr}
              selectedLinkId={selectedLinkId}
              onSelectLink={handleSelectLink}
              notAvailableLabel={notAvailableLabel}
              hideDuplicateWarning={showSuccessMessage}
            />
            <ErrorMessage error={selectionError} errorRef={selectionErrorRef} mt={0} />
          </>
        )}

        <TextField
          fullWidth
          multiline
          minRows={2}
          placeholder={t("erps.billermanagement.bcrreview.reviewernote.placeholder", "Optional reviewer note…")}
          value={reviewerNote}
          onChange={(e) => setReviewerNote(e.target.value)}
        />

        <HStack spacing={2} justifyContent="flex-end">
          <ERPSBaseButton
            variant="secondary"
            label={t("erps.newuser.button.back", "Back")}
            onClick={navigateBack}
          />
          <ERPSBaseButton
            variant="secondary"
            label={t("erps.billermanagement.bcrreview.button.rejectlink", "Reject link")}
            onClick={handleRejectClick}
            loading={loading}
            isDisabled={isSubmitted}
          />
          <ERPSBaseButton
            variant="primary"
            label={t("erps.billermanagement.bcrreview.button.approvelink", "Approve link")}
            onClick={handleApproveClick}
            loading={loading}
            isDisabled={isSubmitted}
          />
        </HStack>
      </VStack>

      <Dialog
        open={isConfirmDialogOpen}
        aria-labelledby="bcr-review-confirm-dialog-title"
        aria-describedby="bcr-review-confirm-dialog-description"
        fullWidth={true}
      >
        <DialogHeader
          id="bcr-review-confirm-dialog-title"
          onClose={() => setIsConfirmDialogOpen(false)}
          className={"box-dialog-header"}
        >
          {getDialogHeader()}
        </DialogHeader>
        <DialogContent id="bcr-review-confirm-dialog-description" className={"dialog-content-size"}>
          {getConfirmationMessageText()}
        </DialogContent>
        <DialogActions className={"box-dialog-buttons"}>
          <ERPSBaseButton
            variant="secondary"
            className="button_with_zeromargin"
            onClick={() => setIsConfirmDialogOpen(false)}
            label={t("erps.common.button.cancel")}
          />
          <ERPSBaseButton
            variant="primary"
            className="button_with_zeromargin"
            onClick={handleConfirm}
            label={t("erps.welcome.page.button.Ok")}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default withSidebar(BcrReviewDetail);
